/**
 * Feedbacks: /admin/feedbacks. Loop-ADMIN-only. Everything people sent
 * through the rail's "Submit Feedback" control during the Alpha, newest
 * first, with who sent it and the screen they were on.
 *
 * Read-only on purpose. There is no reply, triage state or delete here yet:
 * the first question is whether anybody writes in at all, and a workflow
 * built before that is answered is a workflow built for imagined volume.
 *
 * Same gate story as Wait list (screens/admin/Waitlist.tsx): the nav entry is
 * hidden from non-admins and this screen redirects them, but the enforcement
 * is cloud-api's requireAdmin on GET /api/v1/admin/feedback.
 */
import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router';

import { Table, type TableColumn } from '../../components/primitives/Table';
import { EmptyState } from '../../components/patterns/EmptyState';
import { Notice } from '../../components/patterns/Notice';
import { SegmentedControl } from '../../components/patterns/SegmentedControl';
import { Skeleton } from '../../components/patterns/Skeleton';

import { useAccount } from '../../lib/auth/use-app-data';
import { useAsync } from '../../lib/hooks/use-async';
import { apiFetch } from '../../lib/http';
import { relativeTime } from '../../lib/format';
import { t } from '../../lib/i18n/t';
import { captureEvent } from '../../lib/analytics/posthog';

import '../../styles/screens/admin.css';

type FeedbackKind = 'idea' | 'problem' | 'other';
type KindFilter = 'all' | FeedbackKind;

interface FeedbackEntryWire {
  id: string;
  account_id: string;
  account_email: string | null;
  kind: FeedbackKind;
  message: string;
  page_path: string | null;
  impersonated_by: string | null;
  created_at: string;
}

interface ListResponse {
  entries: FeedbackEntryWire[];
  truncated: boolean;
}

async function loadFeedback(): Promise<ListResponse> {
  return apiFetch<ListResponse>('/api/v1/admin/feedback');
}

function kindLabel(kind: FeedbackKind): string {
  switch (kind) {
    case 'idea':
      return t('Idea');
    case 'problem':
      return t('Problem');
    case 'other':
      return t('Other');
  }
}

function when(iso: string): string {
  const rel = relativeTime(iso);
  return rel ? t(rel.key, { n: rel.n }) : '';
}

export default function Feedbacks() {
  const account = useAccount();
  const { data, isLoading, error } = useAsync(loadFeedback, []);
  const [filter, setFilter] = useState<KindFilter>('all');

  useEffect(() => {
    if (account.isAdmin && data) {
      captureEvent('admin_feedbacks_viewed', { total_entries: data.entries.length });
    }
  }, [account.isAdmin, data]);

  const rows = useMemo(
    () => (data ? data.entries.filter((e) => filter === 'all' || e.kind === filter) : []),
    [data, filter],
  );

  if (!account.isAdmin) {
    return <Navigate to="/projects" replace />;
  }

  const columns: TableColumn<FeedbackEntryWire>[] = [
    {
      key: 'when',
      header: t('Sent'),
      render: (row) => <span title={new Date(row.created_at).toLocaleString()}>{when(row.created_at)}</span>,
    },
    {
      key: 'from',
      header: t('From'),
      render: (row) => (
        <>
          {row.account_email ?? row.account_id}
          {row.impersonated_by ? (
            <div className="admin__subdomain">{t('Sent by an admin viewing as this account')}</div>
          ) : null}
        </>
      ),
    },
    { key: 'kind', header: t('Kind'), render: (row) => kindLabel(row.kind) },
    {
      key: 'message',
      header: t('Message'),
      render: (row) => (
        <>
          <p className="admin__message">{row.message}</p>
          {row.page_path ? <div className="admin__subdomain">{row.page_path}</div> : null}
        </>
      ),
    },
  ];

  return (
    <div className="page">
      <header className="page__head">
        <h1 className="page__title">{t('Feedbacks')}</h1>
        <p className="page__lede">{t('What people sent with Submit Feedback, newest first.')}</p>
      </header>

      {data && data.entries.length > 0 && (
        <div className="admin__filter">
          <SegmentedControl<KindFilter>
            label={t('Kind of feedback')}
            value={filter}
            options={[
              { value: 'all', label: t('All') },
              { value: 'idea', label: t('Idea') },
              { value: 'problem', label: t('Problem') },
              { value: 'other', label: t('Other') },
            ]}
            onChange={(next) => {
              captureEvent('admin_feedbacks_filtered', { kind: next });
              setFilter(next);
            }}
          />
        </div>
      )}

      {isLoading && <Skeleton shape="row" count={4} label={t('Loading feedback')} />}

      {error && <Notice tone="attention">{t('Could not load feedback. Try again in a moment.')}</Notice>}

      {data && rows.length === 0 && (
        <EmptyState
          mark="box"
          sentence={data.entries.length === 0 ? t('Nobody has sent feedback yet.') : t('No feedback of this kind.')}
        />
      )}

      {rows.length > 0 && (
        <Table caption={t('Feedbacks')} columns={columns} rows={rows} rowKey={(row) => row.id} />
      )}

      {data?.truncated && <p className="page__lede">{t('Showing the most recent entries only.')}</p>}
    </div>
  );
}
